import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	getParents, hide,
	htmlToElement,
	expressQuerySelectorAll, expressRemoveClass, show
} from "../common/html";
import {postHtmlRequest} from "../utils_modules/fetch";
import {compositeDisposable, IDisposable} from "../utils_modules/disposable";

export interface IPopup {
	popup: HTMLElement;
	openAsync: () => Promise<void>;
	close: () => void;
}

export interface IPopupOptions {
	readonly sendToNetCoreSite?: boolean;
	readonly closeOnClickOutsidePopup?: boolean;
	readonly onClose?: (popupEl?: HTMLElement) => void;
	readonly onBeforeOpen?: (popupEl?: HTMLElement) => void;
	readonly onOpen?: (popupEl?: HTMLElement) => void;
	readonly dialogId: string;
	readonly cssClass?: string;
	readonly view: string;
	readonly forceRefresh?: boolean;
	readonly fromCache?: boolean;
	readonly data?: any;
	readonly closeOnEnter?: boolean;
}

export async function createPopup(options: IPopupOptions): Promise<IPopup> {
	let isOpen = false;
	let popupEl: HTMLElement;
	const eventDisposables: IDisposable = compositeDisposable(undefined);
	const html = expressQuerySelector(document, "html", true);
	const body = expressQuerySelector(document, "body", true);
	const currentY = window.scrollY;

	const {
		closeOnClickOutsidePopup = true,
		onClose,
		onBeforeOpen,
		onOpen,
		dialogId,
		cssClass,
		view,
		forceRefresh,
		fromCache,
		data,
		closeOnEnter
	} = options;

	const create = (): Promise<HTMLElement> => {
		let popup: HTMLElement;
		if (dialogId) {
			popup = expressQuerySelector(document, "#" + dialogId, false);
			if (popup) {
				if (forceRefresh) {
					popup.remove();
				} else {
					return Promise.resolve(popup);
				}
			}
		}

		// Get the html from the server
		const baseApiPath = options.sendToNetCoreSite ? 'apicore' : 'ajaxxhr';
		return postHtmlRequest(`/${baseApiPath}/Dialog/${view}`, data, {headers: new Headers({'Content-type': 'application/json; charset=utf-8'})}).then(
			res => {
				const renderedDialogEl = expressQuerySelector(document, ".renderedDialogs", true);
				popup = renderedDialogEl.appendChild(htmlToElement(res)) as HTMLElement;

				if (dialogId)
					popup.setAttribute("id", dialogId);

				if (cssClass)
					expressAddClass(popup, cssClass);

				return Promise.resolve(popup);
			});
	};

	const close = () => {
		hide(popupEl);
		isOpen = false;
		expressRemoveClass(html, "has-open-dialog u-no-scroll");
		expressRemoveClass(body, "u-no-scroll");
		document.documentElement.style.marginTop = "0px";
		window.scroll(0, currentY);
		eventDisposables.dispose();
		if (onClose) window.setTimeout(() => {
			if (!isOpen) onClose(popupEl);
		}, 200);
	};

	const onMouseDownEvent = (ev) => {
		if (ev.target.id && getParents(ev.target, "#" + popupEl.id).length < 1 && isOpen) close();
	};

	const onKeyDownEvent = (ev) => {
		if (ev.which === 13 || ev.keyCode === 13) if (isOpen) close();
	};

	const onCloseEvent = () => {
		if (isOpen) close();
	};

	const addOnClose = () => {
		const closeButtons = expressQuerySelectorAll<HTMLElement>(popupEl, ".technical-popup-close");
		closeButtons.forEach(closeButton => {
			eventDisposables.add(expressEventListener(closeButton, 'click', onCloseEvent));
		});
		if (closeOnClickOutsidePopup) {
			getParents(popupEl).forEach(el => {
				eventDisposables.add(expressEventListener(el, "mousedown", onMouseDownEvent));
			});
		}
		if (closeOnEnter) eventDisposables.add(expressEventListener(document, "keydown", onKeyDownEvent));
	};

	const openPopupAsync = (): Promise<void> => {
		if (isOpen) return Promise.resolve();
		if (onBeforeOpen) onBeforeOpen(popupEl);
		show(popupEl);
		isOpen = true;
		addOnClose();
		document.documentElement.style.marginTop = "-" + Math.round(currentY) + "px";

		expressAddClass(html, "has-open-dialog u-no-scroll");
		expressAddClass(body, "u-no-scroll");

		popupEl.style.display = "flex";

		if (onOpen) window.setTimeout(() => {
			if (isOpen) onOpen(popupEl);
		}, 200);

		return Promise.resolve();
	};

	if (!popupEl) popupEl = await create();

	return Promise.resolve({
		popup: popupEl,
		openAsync: openPopupAsync,
		close: close
	} as IPopup);
}
